import { useEffect } from 'react'
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine'
import { draggable, dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter'
import type { RefObject } from 'react'
import type { PageDropPosition, PageReorderEvent } from './usePageReorder'
import { createDragExtractor } from '@/lib/utils/dragDrop'

const extractPageDragData = createDragExtractor('page')

export type PageDropIndicator = { id: string; position: PageDropPosition } | null

/** Top quarter drops before, bottom quarter after, the middle nests inside. */
export function getDropPosition(element: HTMLElement, clientY: number): PageDropPosition {
  const rect = element.getBoundingClientRect()
  const ratio = (clientY - rect.top) / rect.height
  if (ratio < 0.25) return 'before'
  if (ratio > 0.75) return 'after'
  return 'child'
}

interface UsePageRowDragDropOptions {
  pageId: string
  rowRef: RefObject<HTMLElement | null>
  handleRef: RefObject<HTMLElement | null>
  dropIndicatorId: string | null
  onDragStart: (id: string) => void
  onDragEnd: () => void
  onReorder: (event: PageReorderEvent) => void
  onDropIndicatorChange: (indicator: PageDropIndicator) => void
}

/**
 * Drag a page row by its handle and drop it before, after or inside another
 * row. Shared by the sidebar page tree and the docs outline table (design
 * 4c) so both re-parent the same way.
 */
export function usePageRowDragDrop({
  pageId,
  rowRef,
  handleRef,
  dropIndicatorId,
  onDragStart,
  onDragEnd,
  onReorder,
  onDropIndicatorChange,
}: UsePageRowDragDropOptions) {
  useEffect(() => {
    const element = rowRef.current
    const dragHandle = handleRef.current
    if (!element || !dragHandle) return

    return combine(
      draggable({
        element,
        dragHandle,
        getInitialData: () => ({ type: 'page', id: pageId }),
        onDragStart: () => {
          onDragStart(pageId)
          onDropIndicatorChange(null)
        },
        onDrop: () => {
          onDragEnd()
          onDropIndicatorChange(null)
        },
      }),
      dropTargetForElements({
        element,
        canDrop: ({ source }) => {
          const data = extractPageDragData(source.data)
          return data !== null && data.id !== pageId
        },
        getData: () => ({ type: 'page', id: pageId }),
        onDropTargetChange: ({ location, source, self }) => {
          const itemData = extractPageDragData(source.data)
          if (!itemData || itemData.id === pageId) {
            if (dropIndicatorId === pageId) onDropIndicatorChange(null)
            return
          }
          const position = getDropPosition(self.element as HTMLElement, location.current.input.clientY)
          onDropIndicatorChange({ id: pageId, position })
        },
        onDragLeave: () => {
          if (dropIndicatorId === pageId) onDropIndicatorChange(null)
        },
        onDrop: ({ location, source, self }) => {
          const itemData = extractPageDragData(source.data)
          onDropIndicatorChange(null)
          if (!itemData || itemData.id === pageId) return

          const innermostTarget = location.current.dropTargets[0]
          if (innermostTarget?.element !== self.element) return

          const position = getDropPosition(self.element as HTMLElement, location.current.input.clientY)
          onReorder({ sourceId: itemData.id, targetId: pageId, dropPosition: position })
        },
      }),
    )
  }, [pageId, rowRef, handleRef, onDragStart, onDragEnd, onReorder, onDropIndicatorChange, dropIndicatorId])
}
